import type { FilterControl, FilterOptionsLoad, FilterSelection } from '../../../types/filterControl';
import type { FilterSelectOption } from '../../../types/table';
import {
  excludedParam,
  includedValues,
  parseNumber,
  selectionParam,
  type SelectionState,
  type Stored,
  storedParam,
  storedSelection,
  storedValue,
  toStored,
  toUrlKey
} from './filterSelection';
import {
  type ControlText,
  excludedKeyOf,
  type FilterOptionsInput,
  type FilterSpec,
  nonEmpty,
  parseAll,
  type QuickFilterSpec,
  type UrlValues
} from './filterSpecs';
import { brand } from './filterUnitType';

// Multi- and single-select specs: options static or from a source, include/exclude kept consistent.

/** URL text back to an option value: by the static options, else by the source's value type. */
const optionParser = <T extends string | number>(input: FilterOptionsInput<T>) => {
  if (input.options) {
    const byText = new Map(input.options.map((option) => [String(option.value), option.value]));
    return { parse: (value: string) => byText.get(value), signature: `options:${[...byText.keys()].join(',')}` };
  }
  const isNumber = input.source.valueType === 'number';
  return {
    parse: (value: string) => (isNumber ? (parseNumber(value) as T | undefined) : (value as T)),
    signature: `source:${input.source.valueType}`
  };
};

/** What the sidebar shows of the options: the list, plus load state and search for a source. */
const optionFields = <T extends string | number>(
  input: FilterOptionsInput<T>
): { options: FilterSelectOption[]; load?: FilterOptionsLoad } => {
  if (input.options) {
    return { options: input.options };
  }
  return { options: input.source.options, load: input.source.load };
};

const sameValues = <T>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

type MultiSelectArgs<T extends string | number> = {
  key: string;
  exclude: boolean;
  input: FilterOptionsInput<T>;
  defaults: readonly T[] | undefined;
  /** A single-select control over an array param: picking one replaces the selection. */
  single: boolean;
  quickFilter: QuickFilterSpec<T> | undefined;
  text: ControlText & { defaultVisibleOptions?: number };
};

export const multiSelectSpec = <T extends string | number>({
  key,
  exclude,
  input,
  defaults = [],
  single,
  quickFilter,
  text
}: MultiSelectArgs<T>): FilterSpec<object> => {
  const { parse, signature } = optionParser(input);
  const excludedKey = excludedKeyOf(key);
  const includeUrlKey = toUrlKey(key);
  const excludeUrlKey = toUrlKey(excludedKey);
  const hasDefaults = defaults.length > 0;
  const readState = (values: UrlValues): SelectionState<T> => ({
    include: values[includeUrlKey] as Stored<T[]>,
    exclude: exclude ? ((values[excludeUrlKey] as T[] | undefined) ?? []) : []
  });

  return {
    kind: 'filter-spec',
    signature: `${key}:${exclude}:${signature}`,
    controlIds: [key],
    urlParams: {
      [includeUrlKey]: selectionParam(parse),
      ...(exclude && { [excludeUrlKey]: excludedParam(parse) })
    },
    resolve: (values, setValues) => {
      const state = readState(values);
      // A single-select control shows one value, so it sends at most one (a link may repeat the key).
      const included = includedValues(state, defaults).slice(0, single ? 1 : undefined);
      const write = ({ include, exclude: excluded }: SelectionState<T>) =>
        setValues(() => ({ [includeUrlKey]: include, ...(exclude && { [excludeUrlKey]: excluded }) }));
      /**
       * One write per change, never include and exclude apart. An excluded value leaves the selection; a selection
       * the user did not touch keeps following the default instead of freezing it into the URL.
       */
      const change = (picked: (string | number)[], pickedExclude: (string | number)[]) => {
        const nextExclude = exclude ? parseAll(pickedExclude, parse) : [];
        const nextInclude = parseAll(picked, parse).filter((value) => !nextExclude.includes(value));
        const untouched = state.include === undefined && sameValues(nextInclude, included);
        write({
          include: untouched ? undefined : storedSelection(nextInclude, hasDefaults),
          exclude: nextExclude
        });
      };
      const common = {
        ...text,
        ...optionFields(input),
        id: key,
        canExclude: exclude,
        reset: () => {
          write({ include: undefined, exclude: [] });
          optionFields(input).load?.onSearch?.('');
        },
        isInitial: state.include === undefined && state.exclude.length === 0
      };
      const control: FilterControl = single
        ? {
            ...common,
            type: 'single-select',
            value: { include: included[0] ?? null, exclude: state.exclude },
            onChange: (next: FilterSelection<string | number | null, (string | number)[]>) =>
              change(next.include == null ? [] : [next.include], next.exclude)
          }
        : {
            ...common,
            type: 'multi-select',
            value: { include: included, exclude: state.exclude },
            onChange: (next: FilterSelection<(string | number)[]>) => change(next.include, next.exclude)
          };
      const tray = quickFilter && {
        label: quickFilter.label,
        options: quickFilter.options,
        // Highlights nothing while the selection is one the tray cannot express (another set, any exclusion).
        selectedValue:
          state.exclude.length > 0
            ? null
            : (quickFilter.options.find((option) => sameValues(option.values ?? [], included))?.value ?? null),
        onChange: (value: string) => {
          const option = quickFilter.options.find((candidate) => candidate.value === value);
          write({ include: storedSelection(option?.values ?? [], hasDefaults), exclude: [] });
        }
      };
      return brand({
        controls: [control],
        quickFilter: tray,
        apiInput: { [key]: nonEmpty(included), ...(exclude && { [excludedKey]: nonEmpty(state.exclude) }) }
      });
    }
  };
};

/** One value of a scalar param. Exclusions, when the endpoint takes a list of them, are a list. */
export const singleSelectSpec = <T extends string | number>({
  key,
  exclude,
  input,
  defaultValue,
  text
}: {
  key: string;
  /** `list`: `excluded<Key>` takes several values; `one`: it takes one, so excluding another replaces it. */
  exclude: false | 'list' | 'one';
  input: FilterOptionsInput<T>;
  defaultValue: T | undefined;
  text: ControlText & { defaultVisibleOptions?: number };
}): FilterSpec<object> => {
  const { parse, signature } = optionParser(input);
  const excludedKey = excludedKeyOf(key);
  const urlKey = toUrlKey(key);
  const excludeUrlKey = toUrlKey(excludedKey);
  const hasDefault = defaultValue !== undefined;
  return {
    kind: 'filter-spec',
    signature: `${key}:${exclude}:single:${signature}`,
    controlIds: [key],
    urlParams: {
      [urlKey]: storedParam<T>(String, parse),
      ...(exclude && { [excludeUrlKey]: excludedParam(parse) })
    },
    resolve: (values, setValues) => {
      const storedExcluded = exclude ? ((values[excludeUrlKey] as T[] | undefined) ?? []) : [];
      const excluded = exclude === 'one' ? storedExcluded.slice(-1) : storedExcluded;
      const stored = storedValue(values[urlKey] as Stored<T>, defaultValue);
      // An excluded value is never the selection.
      const value = stored !== undefined && !excluded.includes(stored) ? stored : undefined;
      const fields = optionFields(input);
      return brand({
        controls: [
          {
            ...text,
            ...fields,
            id: key,
            type: 'single-select',
            canExclude: exclude !== false,
            value: { include: value ?? null, exclude: excluded },
            onChange: (next) => {
              const parsedExclude = exclude ? parseAll(next.exclude, parse) : [];
              const nextExclude = exclude === 'one' ? parsedExclude.slice(-1) : parsedExclude;
              const parsed = next.include == null ? undefined : parse(String(next.include));
              const nextValue = parsed !== undefined && !nextExclude.includes(parsed) ? parsed : undefined;
              // Compared with what the control shows, so clearing or swapping an exclusion keeps an untouched default.
              const untouched = values[urlKey] === undefined && nextValue === value;
              setValues(() => ({
                [urlKey]: untouched ? undefined : toStored(nextValue, hasDefault),
                ...(exclude && { [excludeUrlKey]: nextExclude })
              }));
            },
            reset: () => {
              setValues(() => ({ [urlKey]: undefined, ...(exclude && { [excludeUrlKey]: [] }) }));
              fields.load?.onSearch?.('');
            },
            isInitial: values[urlKey] === undefined && excluded.length === 0
          }
        ],
        apiInput: {
          [key]: value,
          ...(exclude && { [excludedKey]: exclude === 'one' ? excluded[0] : nonEmpty(excluded) })
        }
      });
    }
  };
};
